/**
 * One in-flight run at a time (JOSH-89, R9).
 *
 * The slot is freed when the run settles. A caller's timeout only stops that
 * caller from waiting: a late caller still joins the same run instead of
 * starting a second one.
 */

/** Wraps `run` so concurrent calls share one pending promise. */
export function createSingleFlight<T>(run: () => Promise<T>): () => Promise<T> {
  let inflight: Promise<T> | null = null;
  return () => {
    if (!inflight) {
      const current = run().finally(() => {
        if (inflight === current) inflight = null;
      });
      inflight = current;
    }
    return inflight;
  };
}

/** Resolves with `promise`, or with `fallback` after `ms` if it is still pending. */
export function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
